import axios, { AxiosError, AxiosInstance, AxiosResponse, ResponseType } from 'axios';
import JSZip from 'jszip';
import _ from 'lodash';
import { ENV } from 'src/config/env';
import UUID from 'uuidjs';

axios.defaults.baseURL = ENV.baseUrl;

export function setBaseUrl(url: string) {
    axios.defaults.baseURL = url;
}

const CONTEXT_KEY = 'context';
const CUSTOMER_ID_KEY = 'customerId';

let isCustomerHeaderAllowed = false;

function clearCustomerIdHeader() {
    delete axios.defaults.headers.common.customerId;
    window.sessionStorage.removeItem(CUSTOMER_ID_KEY);
}

export function getContext(): string {
    return window.sessionStorage.getItem(CONTEXT_KEY) || 'customer';
}

export function isAdminContext(): boolean {
    return getContext() === 'admin';
}

export function setContext(context: string, isLoggedIn = false) {
    window.sessionStorage.setItem(CONTEXT_KEY, context);
    isCustomerHeaderAllowed = isLoggedIn;
    if (context !== 'admin' || !isCustomerHeaderAllowed) {
        clearCustomerIdHeader();
    }
}

export function getCustomerId(): string {
    return window.sessionStorage.getItem(CUSTOMER_ID_KEY) || 'current';
}

export function setCustomerId(customerId: string) {
    if (!isCustomerHeaderAllowed) {
        clearCustomerIdHeader();
        return;
    }
    window.sessionStorage.setItem(CUSTOMER_ID_KEY, customerId);
    axios.defaults.headers.common.customerId = customerId;
}

/**
 * Represents key value pairs as strings for request headers
 */
export interface RequestHeaders {
    [name: string]: string;
}

/**
 * Represents key value pairs for request uri params
 */
export interface Params {
    [name: string]: string | number;
}

export interface APIError {
    code: number;
    message: string;
    method: HttpMethod;
    axios: AxiosError;
    /** Structured backend discriminator (e.g. OVER_INVOICING) — additive, most errors carry none. */
    errorCode?: string;
}

export interface CallApiResult<T = any> {
    response?: AxiosResponse<T>;
    error?: APIError;
}

export interface CallApiConfig {
    method?: HttpMethod;
    params?: Params;
    data?: any;
    headers?: RequestHeaders;
    responseType?: ResponseType;
}

const JSON_HEADERS: RequestHeaders = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
};

const CSRF_HEADER = 'X-CSRF-TOKEN';

export const IF_MODIFIED_SINCE_HEADER = 'If-Modified-Since';
export const IF_MATCH_HEADER = 'If-Match';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export function callOtherApi<T>(endpoint: string, config: CallApiConfig = {}): Promise<CallApiResult<T>> {
    const axiosInstance = axios.create({
        ...axios.defaults,
        baseURL: ENV.otherBaseUrl,
    });

    return callApi(endpoint, config, axiosInstance);
}

export function callInternalApi<T>(endpoint: string, config: CallApiConfig = {}): Promise<CallApiResult<T>> {
    const axiosInstance = axios.create({
        ...axios.defaults,
        baseURL: ENV.internalBaseUrl,
    });

    return callApi(endpoint, config, axiosInstance);
}
export function callMailApi<T>(endpoint: string, config: CallApiConfig = {}): Promise<CallApiResult<T>> {
    const axiosInstance = axios.create({
        ...axios.defaults,
        baseURL: ENV.mailBaseUrl,
    });

    return callApi(endpoint, config, axiosInstance);
}
export function callLogisticApi<T>(endpoint: string, config: CallApiConfig = {}): Promise<CallApiResult<T>> {
    const axiosInstance = axios.create({
        ...axios.defaults,
        baseURL: ENV.logisticBaseUrl,
    });

    return callApi(endpoint, config, axiosInstance);
}
export function callTemplateApi<T>(endpoint: string, config: CallApiConfig = {}): Promise<CallApiResult<T>> {
    const axiosInstance = axios.create({
        ...axios.defaults,
        baseURL: ENV.templateBaseUrl,
    });

    return callApi(endpoint, config, axiosInstance);
}

/**
 * Executes API calls and returns a promise with the response in the appropriate format.
 *
 * Supports JSON and FormData bodies.
 * The additionalHeaders will be added to the request headers.
 */
export default function callApi<T>(
    endpoint: string,
    config: CallApiConfig = {},
    axiosInstance?: AxiosInstance
): Promise<CallApiResult<T>> {
    if (!endpoint) throw new Error('Missing endpoint');

    // spread config
    const { method = 'GET', data, params, headers, responseType } = config;

    // Add the additionalHeaders, and if the body isn't FormData, add JSON headers
    const requestHeaders: RequestHeaders =
        data instanceof FormData ? _.assign({}, headers) : _.assign({}, JSON_HEADERS, headers);

    // CSRF protection: generate random UUID and submit it in a custom header
    if (method !== 'GET') {
        requestHeaders[CSRF_HEADER] = UUID.generate();
    }

    return (axiosInstance ? axiosInstance : axios)
        .request({
            url: endpoint,
            data,
            headers: requestHeaders,
            responseType,
            method,
            params,
        })
        .then(response => {
            return {
                response,
            };
        })
        .catch(error => {
            return {
                error: extractAxiosError(method, error),
            };
        });
}

export const extractAxiosError = (method: HttpMethod, error: AxiosError): APIError => {
    // RFC-7807 problem+json (ordering V2 guards) carries the reason in `detail`; never read
    // `details` (plural) — that is the ExceptionResponse request path, not a message.
    const detail = error.response?.data?.detail;
    const message =
        error.response?.data?.message ??
        (typeof detail === 'string' ? detail : undefined) ??
        error.message ??
        'Unknown Error';
    const code = error.response?.status ?? error.request?.status ?? -1;
    const errorCode = error.response?.data?.errorCode;

    return {
        code,
        message,
        axios: error,
        method,
        ...(typeof errorCode === 'string' ? { errorCode } : {}),
    };
};

/**
 * Resolves the message to show for a finished request: the backend's own message when present
 * (e.g. a data-integrity or business-rule reason), otherwise the caller's translated fallback.
 * Reusable across any view/dialog/saga that surfaces a {@link CallApiResult} in a snackbar.
 */
export const apiErrorMessage = (result: { error?: APIError }, fallback?: string): string =>
    result.error?.message?.trim() || fallback || '';

export async function downloadInternal(src: string, method?: HttpMethod, data?: unknown): Promise<Blob> {
    const axiosInstance = axios.create({
        ...axios.defaults,
        baseURL: ENV.internalBaseUrl,
    });
    return download(src, method, data, axiosInstance);
}

export async function downloadLogistic(src: string, method?: HttpMethod, data?: unknown): Promise<Blob> {
    const axiosInstance = axios.create({
        ...axios.defaults,
        baseURL: ENV.logisticBaseUrl,
    });
    return download(src, method, data, axiosInstance);
}

export async function download(
    src: string,
    method?: HttpMethod,
    data?: unknown,
    axiosInstance?: AxiosInstance
): Promise<Blob> {
    return new Promise((resolve, reject) => {
        callApi(src, { responseType: 'blob', method, data }, axiosInstance).then(result => {
            if (result.error || result.response == null) {
                reject(result.error);
                return;
            }

            const fileName = fileNameFromHeader(result.response.headers['content-disposition']);
            const data = result.response.data as Blob;

            saveFile(data, fileName);
            resolve(data);
        });
    });
}

/**
 * Like download(), but also returns the value of one custom response header
 * (axios lowercases header names). Used e.g. for the Lohnverrechnung export's X-Lv-Run-Id.
 */
export async function downloadWithHeader(
    src: string,
    headerName: string,
    method?: HttpMethod,
    data?: unknown
): Promise<{ blob: Blob; header?: string }> {
    const result = await callApi(src, { responseType: 'blob', method, data });
    if (result.error || result.response == null) {
        throw result.error;
    }

    const fileName = fileNameFromHeader(result.response.headers['content-disposition']);
    const blob = result.response.data as Blob;
    saveFile(blob, fileName);

    return { blob, header: result.response.headers[headerName.toLowerCase()] };
}

export function saveFile(blob: Blob, fileName: string) {
    const tmpLink = document.createElement('a');
    tmpLink.href = window.URL.createObjectURL(blob);
    tmpLink.download = fileName;
    tmpLink.id = fileName;
    tmpLink.style.display = 'none';
    tmpLink.setAttribute('download', fileName);

    document.body.appendChild(tmpLink);
    tmpLink.click();
    document.body.removeChild(tmpLink);
    // without the revoke every download pins its blob in memory until page unload
    window.URL.revokeObjectURL(tmpLink.href);
}

export function fileNameFromHeader(header: string): string {
    const reg = /filename=[\\"]{1,2}(.*)[\\"]{1,2}/g;
    const matches = reg.exec(header);

    return matches?.[1] ?? 'filename';
}

export async function downloadAsZIP(srcArray: string[]): Promise<Blob> {
    return new Promise((resolve, reject) => {
        const zip = new JSZip();
        srcArray.forEach(src => {
            callApi(src, { responseType: 'blob' }).then(result => {
                if (result.error || result.response == null) {
                    reject(result.error);
                    return;
                }

                const fileName = fileNameFromHeader(result.response.headers['content-disposition']);
                const data = result.response.data as Blob;
                zip.file(fileName, data);

                if (Object.keys(zip.files).length === srcArray.length) {
                    zip.generateAsync({ type: 'blob' }).then(
                        function (blob) {
                            const tmpLink = document.createElement('a');
                            tmpLink.href = window.URL.createObjectURL(blob);
                            tmpLink.setAttribute('download', 'invoiceDownload.zip');
                            tmpLink.setAttribute('id', srcArray[0]);
                            tmpLink.style.display = 'none';

                            document.body.appendChild(tmpLink);
                            tmpLink.click();
                            document.body.removeChild(tmpLink);
                            resolve(blob);
                        },
                        function (err) {
                            reject(err);
                        }
                    );
                }
            });
        });
    });
}
